import { Injectable } from '@angular/core';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const UTM_PARAM_KEYS: (keyof UtmParams)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
];

const UTM_COOKIE_EXPIRY_MINUTES = 60;
const UTM_COOKIE_DOMAIN = '.cosentino.com';

@Injectable({
  providedIn: 'root'
})
export class UtmService {

  /**
   * Sets a cookie with the specified name, value and expiration time
   * @param name - Cookie name
   * @param value - Cookie value
   * @param minutes - Expiration time in minutes
   */
  private setCookie(name: string, value: string, minutes: number): void {
    const date = new Date();
    date.setTime(date.getTime() + (minutes * 60 * 1000));
    const expires = 'expires=' + date.toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';' + expires +
      ';path=/' +
      ';domain=' + UTM_COOKIE_DOMAIN +
      ';SameSite=Lax' +
      ';Secure';
  }

  /**
   * Gets a cookie value by name
   * @param name - Cookie name
   * @returns Cookie value or null if not found
   */
  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Deletes a cookie by name
   * @param name - Cookie name
   */
  private deleteCookie(name: string): void {
    document.cookie = name + '=' +
      ';expires=Thu, 01 Jan 1970 00:00:00 UTC' +
      ';path=/' +
      ';domain=' + UTM_COOKIE_DOMAIN +
      ';SameSite=Lax' +
      ';Secure';
  }

  /**
   * Captures UTM parameters from the given URL and saves them to sessionStorage and cookies.
   * Only saves if at least one UTM parameter is present.
   * @param url - The URL to extract UTM parameters from
   * @returns The captured UTM parameters, or null if none were found
   */
  captureFromUrl(url: URL): UtmParams | null {
    const utmParams: UtmParams = {};
    let hasUtmParams = false;

    for (const key of UTM_PARAM_KEYS) {
      const value = url.searchParams.get(key);
      if (value) {
        utmParams[key] = value;
        hasUtmParams = true;
      }
    }

    if (hasUtmParams) {
      this.save(utmParams);
      console.log('[UTM] Captured UTM parameters:', utmParams);
      return utmParams;
    }

    return null;
  }

  /**
   * Saves UTM parameters to sessionStorage and cookies as individual keys
   * @param params - The UTM parameters to save
   */
  save(params: UtmParams): void {
    try {
      for (const key of UTM_PARAM_KEYS) {
        if (params[key]) {
          sessionStorage.setItem(key, params[key]!);
          this.setCookie(key, params[key]!, UTM_COOKIE_EXPIRY_MINUTES);
        }
      }
    } catch (error) {
      console.error('[UTM] Error saving UTM parameters:', error);
    }
  }

  /**
   * Retrieves UTM parameters from sessionStorage
   * @returns The stored UTM parameters, or null if none exist
   */
  get(): UtmParams | null {
    try {
      const params: UtmParams = {};
      let hasAnyParam = false;

      for (const key of UTM_PARAM_KEYS) {
        const value = sessionStorage.getItem(key);
        if (value) {
          params[key] = value;
          hasAnyParam = true;
        }
      }

      return hasAnyParam ? params : null;
    } catch (error) {
      console.error('[UTM] Error reading UTM parameters from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Clears UTM parameters from sessionStorage and cookies
   */
  clear(): void {
    try {
      for (const key of UTM_PARAM_KEYS) {
        sessionStorage.removeItem(key);
        this.deleteCookie(key);
      }
    } catch (error) {
      console.error('[UTM] Error clearing UTM parameters:', error);
    }
  }

  /**
   * Checks if UTM parameters exist in sessionStorage
   * @returns true if UTM parameters are stored
   */
  hasUtmParams(): boolean {
    return this.get() !== null;
  }
}
